import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { getReport, retryReport } from '../../api/assessment';
import useWrongAnswers from '../../hooks/useWrongAnswers';
import useReportTracking from '../../hooks/useReportTracking';
import PageLayout from '../PageLayout';
import Button from '../Button';
import Alert from '../Alert';
import ReportLoadingScreen from '../ReportLoadingScreen';
import ReportNav from './ReportNav';
import P1Overview from './pages/P1Overview';
import P2Profile from './pages/P2Profile';
import P3DimensionDetail from './pages/P3DimensionDetail';
import PFinalRoadmap from './pages/PFinalRoadmap';
import WrongAnswersDrawer from './WrongAnswersDrawer';
import ReportPageV4 from '../report_v4/ReportPageV4';

const HASH_PREFIX = '#/page/';

function parseHashIdx() {
  const h = window.location.hash || '';
  if (!h.startsWith(HASH_PREFIX)) return 0;
  const n = parseInt(h.slice(HASH_PREFIX.length), 10);
  return Number.isFinite(n) && n >= 1 ? n - 1 : 0;
}

function buildPages(report) {
  const dimAnalysis = report?.narrative_v2?.dimension_analysis || [];
  const dimPages = dimAnalysis.map((da, i) => ({
    id: `p3-${i}`,
    title: `维度 ${i + 1}`,
    kind: 'dim',
    dimensionAnalysis: da,
    dimensionIndex: i,
  }));
  return [
    { id: 'p1', title: '总览', kind: 'p1' },
    { id: 'p2', title: '画像', kind: 'p2' },
    ...dimPages,
    { id: 'final', title: '路线图', kind: 'final' },
  ];
}

export default function ReportPageV2() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const [errorStatus, setErrorStatus] = useState(null);
  const [retryToken, setRetryToken] = useState(0);  // handleRetry 自增触发 polling useEffect 重跑
  const [currentIdx, setCurrentIdx] = useState(() => parseHashIdx());
  const [drawer, setDrawer] = useState({ open: false, dimensionId: null, dimName: '' });
  const scrollerRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const prevFocusRef = useRef(null);
  // ISSUE-004 (2026-04-28): 网络层抖动（ERR_CONNECTION_CLOSED / 502 上游瞬断）算 transient，
  // 计数 <5 继续轮询而不是把用户卡死在"报告加载失败"。
  const transientFailRef = useRef(0);

  const pages = useMemo(() => (report ? buildPages(report) : []), [report]);
  const total = pages.length;
  const wrongAnswers = useWrongAnswers(assessmentId, { enabled: !!report && currentIdx >= 1 });

  // 当前页 → 埋点维度。p3-X 维度页要带 dim_id（取自 dimensionAnalysis.dimension_id），
  // 其他页 dim_id=null。useMemo 让引用稳定，避免 useReportTracking 的 effect 误重跑。
  const currentPage = useMemo(() => {
    if (currentIdx < 0 || currentIdx >= pages.length) return null;
    const p = pages[currentIdx];
    if (p.kind === 'p1') return { pageType: 'overview', dimId: null };
    if (p.kind === 'p2') return { pageType: 'profile', dimId: null };
    if (p.kind === 'final') return { pageType: 'final', dimId: null };
    if (p.kind === 'dim') {
      const dimId = p.dimensionAnalysis?.dimension_id;
      return { pageType: 'dimension', dimId: dimId != null ? Number(dimId) : null };
    }
    return null;
  }, [pages, currentIdx]);

  const { reportComplete } = useReportTracking({
    assessmentId,
    currentPage,
    enabled: !!report,
  });

  // 末页到达 → report_complete（hook 内部 sessionStorage flag 去重）
  useEffect(() => {
    if (currentPage?.pageType === 'final') reportComplete();
  }, [currentPage, reportComplete]);

  const fetchReport = useCallback(async () => {
    try {
      const { data } = await getReport(assessmentId);
      if (data?.status === 'pending') {
        setPolling(true);
        transientFailRef.current = 0;
        return false;
      }
      setReport(data);
      setPolling(false);
      transientFailRef.current = 0;
      return true;
    } catch (err) {
      const s = err.response?.status;
      if (s === 202) {
        setPolling(true);
        transientFailRef.current = 0;
        return false;
      }
      // ISSUE-004：没拿到 response（err.response undefined）或 5xx 当 transient，连试 <5 次。
      const isTransient = !err.response || (s >= 500 && s < 600);
      if (isTransient) {
        transientFailRef.current += 1;
        if (transientFailRef.current < 5) {
          setPolling(true);
          return false;
        }
        setErrorStatus(s || 0);
        setError(`报告加载失败（已重试 ${transientFailRef.current} 次，可能后端忙，稍后再来）`);
        setPolling(false);
        return true;
      }
      setErrorStatus(s);
      setError(err.response?.data?.detail || '报告加载失败');
      setPolling(false);
      return true;
    }
  }, [assessmentId]);

  useEffect(() => {
    let timer;
    const run = async () => {
      setLoading(true);
      const done = await fetchReport();
      setLoading(false);
      if (!done) {
        timer = setInterval(async () => {
          const ready = await fetchReport();
          if (ready) clearInterval(timer);
        }, 3000);
      }
    };
    run();
    return () => clearInterval(timer);
  }, [fetchReport, retryToken]);

  const goTo = useCallback((idx) => {
    if (total === 0) return;
    const clamped = Math.max(0, Math.min(idx, total - 1));
    setCurrentIdx(clamped);
    const newHash = `${HASH_PREFIX}${clamped + 1}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
    const el = scrollerRef.current;
    if (el) {
      isProgrammaticScroll.current = true;
      el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
    }
  }, [total]);

  useEffect(() => {
    if (!report) return;
    const onHashChange = () => {
      const idx = parseHashIdx();
      goTo(idx);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [report, goTo]);

  useEffect(() => {
    if (!report) return;
    const onKey = (e) => {
      if (drawer.open) return;
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(currentIdx - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(currentIdx + 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [report, currentIdx, goTo, drawer.open]);

  useEffect(() => {
    if (!drawer.open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawer.open]);

  const handleOpenDrawer = useCallback(({ dimensionId, dimName }) => {
    prevFocusRef.current = document.activeElement;
    setDrawer({ open: true, dimensionId, dimName });
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawer(d => ({ ...d, open: false }));
    const prev = prevFocusRef.current;
    if (prev && typeof prev.focus === 'function') {
      setTimeout(() => prev.focus(), 0);
    }
  }, []);

  // initial scroll sync after pages mount
  useEffect(() => {
    if (!report || total === 0) return;
    const el = scrollerRef.current;
    if (!el) return;
    const initialIdx = Math.max(0, Math.min(currentIdx, total - 1));
    el.scrollTo({ left: initialIdx * el.clientWidth, behavior: 'auto' });
    if (initialIdx !== currentIdx) setCurrentIdx(initialIdx);
    // run once when report becomes available; currentIdx 故意不入 deps，
    // 否则会与 goTo 的 smooth scroll 冲突。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, total]);

  // sync state when user scrolls (mobile scroll-snap, desktop wheel/touchpad)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || total === 0) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (isProgrammaticScroll.current) {
          isProgrammaticScroll.current = false;
          return;
        }
        const w = el.clientWidth;
        if (w === 0) return;
        const idx = Math.round(el.scrollLeft / w);
        if (idx !== currentIdx) {
          const clamped = Math.max(0, Math.min(idx, total - 1));
          setCurrentIdx(clamped);
          const newHash = `${HASH_PREFIX}${clamped + 1}`;
          if (window.location.hash !== newHash) {
            window.history.replaceState(null, '', newHash);
          }
        }
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
    };
  }, [total, currentIdx]);

  const handleRetry = async () => {
    setError('');
    setErrorStatus(null);
    setReport(null);
    setLoading(true);
    try {
      await retryReport(assessmentId);
      setRetryToken(t => t + 1);  // 触发 fetchReport useEffect 重跑，进 polling
    } catch {
      setError('重试失败，请稍后再试');
      setLoading(false);
    }
  };

  if (loading || polling) {
    return <ReportLoadingScreen assessmentId={assessmentId} />;
  }

  if (error || !report) {
    const isNotCompleted = errorStatus === 400 && error?.includes('未完成');
    return (
      <PageLayout>
        <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
          <Alert type="error">{error || '报告不存在'}</Alert>
          {isNotCompleted ? (
            <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Button onClick={() => navigate(`/assessment/${assessmentId}`)}>继续完成测评</Button>
              <Button variant="secondary" onClick={() => navigate('/')}>返回首页</Button>
            </div>
          ) : (
            <Button onClick={handleRetry} style={{ marginTop: 16 }}>重新生成报告</Button>
          )}
        </div>
      </PageLayout>
    );
  }

  // V5：所有 V5 报告（report_version=5）走独立 /report/v5/:id 路由。
  // V5 的 user_report schema 与 V2 多页 narrative_v2 完全不同，不能套 V2 渲染。
  // 列表/弹窗/直访 /report/{id} 统一在此重定向，调用方不用各自判断版本。
  if (report?.report_version === 5) {
    return <Navigate to={`/report/v5/${assessmentId}`} replace />;
  }

  // M13：v4 报告分流到 ReportPageV4。fetch / polling / 错误重试逻辑全部在本主壳，
  // v4 inner 只负责 5 板块渲染 + nav，避免重复请求。
  if (report?.report_version === 4) {
    return (
      <PageLayout>
        <ReportPageV4 report={report} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div data-testid="report-page-v2-root" style={{ maxWidth: 880, margin: '0 auto' }}>
        <ReportNav pages={pages} currentIdx={currentIdx} onGoTo={goTo} />
        <div
          ref={scrollerRef}
          data-testid="report-page-v2-scroller"
          style={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          {pages.map((p) => (
            <section
              key={p.id}
              data-testid={`report-page-v2-slot-${p.id}`}
              style={{
                flex: '0 0 100%',
                scrollSnapAlign: 'start',
                padding: '24px 20px 48px',
                boxSizing: 'border-box',
              }}
            >
              {p.kind === 'p1' && <P1Overview report={report} />}
              {p.kind === 'p2' && <P2Profile report={report} />}
              {p.kind === 'dim' && (
                <P3DimensionDetail
                  report={report}
                  dimensionAnalysis={p.dimensionAnalysis}
                  dimensionIndex={p.dimensionIndex}
                  wrongCount={wrongAnswers.itemsForDimension(p.dimensionAnalysis?.dimension_id).length}
                  onOpenDrawer={handleOpenDrawer}
                />
              )}
              {p.kind === 'final' && <PFinalRoadmap report={report} />}
            </section>
          ))}
        </div>
        <WrongAnswersDrawer
          open={drawer.open}
          dimName={drawer.dimName}
          items={drawer.dimensionId != null ? wrongAnswers.itemsForDimension(drawer.dimensionId) : []}
          loading={wrongAnswers.loading}
          error={wrongAnswers.error}
          onClose={handleCloseDrawer}
        />
      </div>
    </PageLayout>
  );
}
